/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

const API_BASE = environment.apiUrl;

export interface ArchetypeReportPriceRequest {
  surveyResultId: string;
  archetypeReportDiscountName?: string;
  groupId?: string;
  // Preview the cheaper "regenerate" price (Steve 2026-07-22) instead of the
  // full report/upgrade price — used by the regenerate modal.
  isRefresh?: boolean;
}

export interface ArchetypeReportPriceResponse {
  currencyCode: string;
  currencyQuantity: number;
  originalPrice: number;
  lineItems: Array<{ name: string; price: number }>;
  discountApplied?: {
    code: string;
    type: 'flat_fee' | 'percentage';
    percent?: number;
    amount: number;
  };
}

export interface ArchetypeReportPurchaseRequest {
  surveyResultId: string;
  stripeToken?: any;
  price: ArchetypeReportPriceResponse;
  archetypeReportDiscountName?: string;
  purchasedByUserId?: string; // For admin/coach purchases
}

export interface ArchetypeReport {
  _id: string;
  user: string | { _id: string; firstName: string; lastName: string; email: string };
  group?: string;
  surveyResult: string;
  archetype: {
    primary: string;
    secondary?: string;
    scores: { [key: string]: number };
    categoryScores: {
      personality: number;
      strengths: number;
      skills: number;
      spiritualGifts: number;
      passions: number;
      total: number;
    };
    confidence?: 'High' | 'Medium' | 'Low';
    reasonCodes?: string[];
    warnings?: string[];
    disqualifiers?: string[];
    _engineVersion?: string;
  };
  mode: 'faith' | 'values';
  ipsatScore?: number;
  stage?: { name: string; body: string };
  surveyType: { identifier: string; name: string };
  content: {
    primary: any;
    secondary?: any;
  };
  /**
   * AI-personalized sections populated at /purchase time. May be absent
   * if the AI call failed; the modals fall back to "Generating…" placeholders.
   * Shape mirrors ArchetypeReport.model.js aiContent.
   */
  aiContent?: {
    category_key_insights?: {
      personality: string;
      strengths: string;
      skills: string;
      gifts_or_values: string;
      passions: string;
    };
    identity_statement_lenses?: {
      identity_overview: string;
      best_contribution: string;
      developmental_priority: string;
      potential_derailer: string;
      vulnerable_setting: string;
    };
    integrated_insights?: { title: string; body: string }[];
    development_focus?: {
      sustain: string[];
      strengthen: string[];
      serve: string[];
    };
    overlay_synthesis?: string | null;
    final_summary?: string | null;
    model?: string;
    generatedAt?: Date;
  };
  contentVersion: {
    contentVersion: string;
    engineVersion: string;
  };
  price: {
    currencyCode: string;
    currencyQuantity: number;
  };
  stripePaymentId?: string;
  discountCode?: string;
  generatedAt: Date;
  /** Captured at generation; frontend compares against current SurveyResult
   *  to detect statement edits and show the regenerate banner. */
  identityStatementsHash?: string;
  /** True for reports created via /autoGenerate (the bundled-free new-user
   *  path), false/absent for /purchase reports. */
  autoGenerated?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ArchetypeReportService {
  constructor(private http: HttpClient) {}

  /**
   * Get an archetype report by ID
   */
  get(archetypeReportId: string): Observable<ArchetypeReport> {
    return this.http.get<ArchetypeReport>(
      `${API_BASE}archetypeReports/${archetypeReportId}`,
      { withCredentials: true }
    );
  }

  /**
   * Get archetype report by survey result ID
   */
  getBySurveyResult(surveyResultId: string): Observable<ArchetypeReport | null> {
    return this.http.get<ArchetypeReport | null>(
      `${API_BASE}archetypeReports/bySurveyResult/${surveyResultId}`,
      { withCredentials: true }
    );
  }

  /**
   * Public shareable version of an archetype report (Steve 2026-07-07 —
   * powers the 2.0 Share Result link). No auth. Backend strips aiContent
   * + billing metadata; response shape is different from get()/getBySurveyResult().
   */
  getShareableVersion(surveyResultId: string): Observable<any> {
    return this.http.get<any>(
      `${API_BASE}archetypeReports/shareable/${surveyResultId}`
    );
  }

  /**
   * Get all archetype reports for a user
   */
  getByUser(userId: string): Observable<ArchetypeReport[]> {
    return this.http.get<ArchetypeReport[]>(
      `${API_BASE}users/${userId}/archetypeReports`,
      { withCredentials: true }
    );
  }

  /**
   * Calculate price for purchasing an archetype report
   */
  calculatePrice(request: ArchetypeReportPriceRequest): Observable<ArchetypeReportPriceResponse> {
    return this.http.post<ArchetypeReportPriceResponse>(
      `${API_BASE}archetypeReports/calculatePrice`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Purchase an archetype report
   */
  purchase(request: ArchetypeReportPurchaseRequest): Observable<ArchetypeReport> {
    return this.http.post<ArchetypeReport>(
      `${API_BASE}archetypeReports/purchase`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Auto-generate the bundled-free archetype report. Same engine + AI
   * pipeline as /purchase but with no payment. Backend gates on
   * User.eligibleForFreeArchetypeReport + "no prior ArchetypeReport on
   * this user." Frontend calls this after a successful IPSAT submission
   * when the user is flagged eligible (Steve 2026-06-11 item 1).
   */
  autoGenerate(surveyResultId: string): Observable<ArchetypeReport> {
    return this.http.post<ArchetypeReport>(
      `${API_BASE}archetypeReports/autoGenerate`,
      { surveyResultId },
      { withCredentials: true }
    );
  }

  /**
   * Regenerate an existing report against the now-edited 1.0 self-authored
   * statements (Steve 2026-06-11 item 2). A coach-issued discount code
   * usually zeroes the price; if it doesn't, stripeToken is required.
   * Backend deletes the old report and inserts a fresh one keyed to the
   * same SurveyResult.
   */
  regenerate(request: {
    surveyResultId: string;
    discountCode?: string;
    stripeToken?: any;
  }): Observable<ArchetypeReport> {
    return this.http.post<ArchetypeReport>(
      `${API_BASE}archetypeReports/regenerate`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Download the server-rendered PDF of the report, with "Page N of M" page
   * numbers in the footer (Steve 2026-07-27). Chrome can't page-number the
   * browser print of this free-flow report, so the backend renders the same
   * printable route through headless Chrome. Returns the PDF as a Blob.
   */
  getPdf(surveyResultId: string): Observable<Blob> {
    return this.http.get(
      `${API_BASE}archetypeReports/bySurveyResult/${surveyResultId}/pdf`,
      { withCredentials: true, responseType: 'blob' }
    );
  }

  /**
   * Check if a survey result has an archetype report
   */
  exists(surveyResultId: string): Observable<{ exists: boolean; archetypeReportId?: string }> {
    return this.http.get<{ exists: boolean; archetypeReportId?: string }>(
      `${API_BASE}archetypeReports/exists/${surveyResultId}`,
      { withCredentials: true }
    );
  }
}
